import * as React from "react";
import {
  Box,
  Divider,
  Grid,
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Project } from "../../types/project";
import MapPreview from "../../components/MapPreview";
import {
  ContactPhone,
  EditOutlined,
  EmailOutlined,
  ExpandMore,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { nl2br } from "../../utils/helper";
import ModalInternalNotesForm from "./ModalInternalNotesForm";
import { formatWithTimezone } from "../../utils/dateHelper";
import { decode } from "html-entities";
import { CardBase } from "../../components/global";
import { useCardPreferences } from "../../hooks/useCardPreferences";
import { CARD_PREFERENCE_TYPE, CARD_PREFERENCE_PAGE } from "../../constants/cardPreferences";
import { PROJECT_CARD_IDS, DEFAULT_PROJECT_CARD_ORDER } from "../../constants/projectCards";

const ProjectDetailsPanel = ({ data }: { data?: Project; }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formInternalNotesModal, setFormInternalNotesModal] = React.useState<{
    open: boolean;
    data: Project | null;
  }>({
    open: false,
    data: null,
  });
  const [lng, lat] = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer?.coordinate?.coordinates || [];
    } else if (!!data?.location) {
      return data?.location?.coordinate?.coordinates || [];
    } else return [];
  }, [data]);

  // Get card preferences - skip API call, use default layout
  const { preferences } = useCardPreferences({
    type: CARD_PREFERENCE_TYPE.ADMIN,
    page: CARD_PREFERENCE_PAGE.PROJECT,
    skip: true,
  });

  // Get card order and hidden cards from preferences
  const cardOrder = React.useMemo(() => {
    if (preferences && preferences.cardOrder && preferences.cardOrder.length > 0) {
      // Filter out hidden cards and add any missing cards
      const allCardIds = Object.values(PROJECT_CARD_IDS);
      const filteredOrder = preferences.cardOrder.filter(
        (id: string) => allCardIds.includes(id as any)
      );
      const missingCards = DEFAULT_PROJECT_CARD_ORDER.filter(
        (id: string) => !filteredOrder.includes(id)
      );
      return [...filteredOrder, ...missingCards];
    }
    return DEFAULT_PROJECT_CARD_ORDER;
  }, [preferences]);

  const hiddenCards = React.useMemo(() => {
    return preferences?.hiddenCards || [];
  }, [preferences]);

  // Helper function to render card by ID
  const renderCard = (cardId: string) => {
    switch (cardId) {
      case PROJECT_CARD_IDS.scopeOfWork:
        return (
          <CardBase
            key={cardId}
            title="Scope of Work"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              {nl2br(decode(data?.description))}
            </Typography>
          </CardBase>
        );
      case PROJECT_CARD_IDS.confidentialInformation:
        return (
          <CardBase
            key={cardId}
            title="Confidential Information"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              {nl2br(data?.confidential_information) || "n/a"}
            </Typography>
          </CardBase>
        );
      case PROJECT_CARD_IDS.startTime:
        return (
          <CardBase
            key={cardId}
            title="Start Time"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              {formatWithTimezone(data?.start_date, data?.timezone)} ({data?.timezone})
            </Typography>
          </CardBase>
        );
      case PROJECT_CARD_IDS.tasks:
        return (
          <CardBase
            key={cardId}
            title="Tasks"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            {data?.tasks && data?.tasks?.length > 0 ? (
              data?.tasks?.map((it, idx) => {
                const tasksLength = data?.tasks?.length || 0;
                return (
                  <Accordion key={idx} sx={{ mb: idx < tasksLength - 1 ? { xs: 0.75, sm: 1 } : 0 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>{it.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component="div" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                        <>
                        <strong>Description:</strong> {decode(it.description) || "n/a"}
                        <br />
                        <strong>Start:</strong> {it.start_date || "n/a"}
                        </>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Typography sx={{ color: "text.disabled", fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                No tasks specified
              </Typography>
            )}
          </CardBase>
        );
      case PROJECT_CARD_IDS.requirements:
        return (
          <CardBase
            key={cardId}
            title="Requirements"
            useGlassCard={false}
            sx={{ mb: 2 }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#F4A259", fontWeight: 600, mb: 1, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}
            >
              Skills
            </Typography>
            <Box sx={{ mb: 2 }}>
              {!data?.skills || data?.skills?.length == 0 ? (
                <Typography sx={{ color: "text.disabled", fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                  Not Required
                </Typography>
              ) : (
                data?.skills?.map((it) => (
                  <Chip
                    label={it.name}
                    key={it.id}
                    size="small"
                    sx={{
                      mr: { xs: 0.5, sm: 1 },
                      mb: { xs: 0.5, sm: 1 },
                      backgroundColor: "#F4A259",
                      color: "white",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      height: { xs: 24, sm: 32 },
                      "&:hover": {
                        backgroundColor: "#E8944A",
                      },
                    }}
                  />
                ))
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "#F4A259", fontWeight: 600, mb: 1, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}
            >
              Certifications
            </Typography>
            <Box>
              {!data?.certifications || data?.certifications?.length == 0 ? (
                <Typography sx={{ color: "text.disabled", fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                  Not Required
                </Typography>
              ) : (
                data?.certifications?.map((it) => (
                  <Chip
                    label={it.name}
                    key={it.id}
                    size="small"
                    sx={{
                      mr: { xs: 0.5, sm: 1 },
                      mb: { xs: 0.5, sm: 1 },
                      backgroundColor: "#F4A259",
                      color: "white",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      height: { xs: 24, sm: 32 },
                      "&:hover": {
                        backgroundColor: "#E8944A",
                      },
                    }}
                  />
                ))
              )}
            </Box>
          </CardBase>
        );
      case PROJECT_CARD_IDS.additionalSettings:
        return (
          <CardBase
            key={cardId}
            title="Additional Settings"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            <Stack spacing={{ xs: 0.75, sm: 1 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Number of Talents:</strong> {data?.num_of_talent}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Approximate Hours to Complete:</strong> {data?.approximate_hours_to_complete} hour
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Tools Required:</strong> {!!data?.tool_required ? "Yes" : "No"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Mile Radius:</strong> {data?.mile_radius} miles
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Background Check Required:</strong> {!!data?.background_check_required ? "Yes" : "No"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Drug Test Required:</strong> {!!data?.drug_test_required ? "Yes" : "No"}
              </Typography>
            </Stack>
          </CardBase>
        );
      case PROJECT_CARD_IDS.workSite:
        return (
          <CardBase
            key={cardId}
            title="Work Site"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 }, p: 0 }}
          >
            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
              <MapPreview lat={lat} lng={lng} height={isMobile ? "200px" : "300px"} radius={data?.mile_radius} />
            </Box>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
              {!!data?.location && (
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ color: "#F4A259", fontWeight: 600, mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    {data?.location?.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                    {data?.location.address_1}, {data?.location.city}, {data?.location.state} {data?.location.zip}
                  </Typography>
                  <Stack direction={"row"} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    <ContactPhone
                      fontSize={"small"}
                      color={"disabled"}
                      sx={{ mr: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                      {data?.location?.primary_contact_name} - {data?.location?.primary_contact_phone_number}
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} alignItems="center" flexWrap="wrap">
                    <EmailOutlined
                      fontSize={"small"}
                      color={"disabled"}
                      sx={{ mr: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                      {data?.location?.primary_contact_email}
                    </Typography>
                  </Stack>
                </Box>
              )}
              {!!data?.customer && (
                <Box sx={{ mt: !!data?.location ? { xs: 2, sm: 3 } : 0 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#F4A259", fontWeight: 600, mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    {data?.customer?.first_name} {data?.customer?.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                    {data?.customer.physical_address_1}, {data?.customer.city}, {data?.customer.state} {data?.customer.zip}
                  </Typography>
                  <Stack direction={"row"} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    <ContactPhone
                      fontSize={"small"}
                      color={"disabled"}
                      sx={{ mr: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                      {data?.customer?.phone_number}
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} alignItems="center" flexWrap="wrap">
                    <EmailOutlined
                      fontSize={"small"}
                      color={"disabled"}
                      sx={{ mr: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    />
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                      {data?.customer?.email}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          </CardBase>
        );
      case PROJECT_CARD_IDS.manager:
        return (
          <CardBase
            key={cardId}
            title="Manager"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            {data?.manager ? (
              <Box>
                <Typography
                  variant="body1"
                  sx={{ color: "#F4A259", fontWeight: 600, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  {data?.manager?.first_name} {data?.manager?.last_name}
                </Typography>
                <Stack direction={"row"} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                  <ContactPhone
                    fontSize={"small"}
                    color={"disabled"}
                    sx={{ mr: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  />
                  <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                    {data?.manager?.phone_number}
                  </Typography>
                </Stack>
                <Stack direction={"row"} alignItems="center" flexWrap="wrap">
                  <EmailOutlined
                    fontSize={"small"}
                    color={"disabled"}
                    sx={{ mr: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  />
                  <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                    {data?.manager?.email}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "text.disabled", fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                No manager assigned
              </Typography>
            )}
          </CardBase>
        );
      case PROJECT_CARD_IDS.notesBetweenCustomerAndAdmin:
        return (
          <CardBase
            key={cardId}
            title="Notes Between Customer and Admin"
            useGlassCard={false}
            headerActions={
              <IconButton
                size="small"
                onClick={() => setFormInternalNotesModal({ open: true, data: data! })}
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                <EditOutlined />
              </IconButton>
            }
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              {nl2br(data?.internal_notes || "n/a")}
            </Typography>
          </CardBase>
        );
      case PROJECT_CARD_IDS.meta:
        return (
          <CardBase
            key={cardId}
            title="Meta"
            useGlassCard={false}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          >
            <Stack spacing={{ xs: 0.75, sm: 1 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Created At:</strong> {dayjs(data?.created_at).format("MMM DD, YYYY hh:mm A")}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                <strong>Last Updated:</strong> {dayjs(data?.updated_at).format("MMM DD, YYYY hh:mm A")}
              </Typography>
            </Stack>
          </CardBase>
        );
      default:
        return null;
    }
  };

  // Separate cards into left column (md={8}) and right column (md={4})
  const leftColumnCards: string[] = [
    PROJECT_CARD_IDS.scopeOfWork,
    PROJECT_CARD_IDS.confidentialInformation,
    PROJECT_CARD_IDS.startTime,
    PROJECT_CARD_IDS.tasks,
    PROJECT_CARD_IDS.requirements,
    PROJECT_CARD_IDS.additionalSettings,
  ];
  const rightColumnCards: string[] = [
    PROJECT_CARD_IDS.workSite,
    PROJECT_CARD_IDS.manager,
    PROJECT_CARD_IDS.notesBetweenCustomerAndAdmin,
    PROJECT_CARD_IDS.meta,
  ];

  // Filter and order cards
  const visibleLeftCards = cardOrder.filter(
    (id: string) => !hiddenCards.includes(id) && leftColumnCards.includes(id)
  );
  const visibleRightCards = cardOrder.filter(
    (id: string) => !hiddenCards.includes(id) && rightColumnCards.includes(id)
  );

  return (
    <Box>
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={12} md={8}>
          {visibleLeftCards.map((cardId) => renderCard(cardId))}
        </Grid>
        <Grid item xs={12} md={4}>
          {visibleRightCards.map((cardId) => renderCard(cardId))}
        </Grid>
      </Grid>
      <ModalInternalNotesForm
        open={formInternalNotesModal.open}
        onClose={() => setFormInternalNotesModal({ open: false, data: null })}
        project={formInternalNotesModal.data}
      />
    </Box>
  );
};
export default ProjectDetailsPanel;
